import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Loader2,
  Mail,
  Phone,
  Trash2,
  Inbox,
  ShieldCheck,
} from "lucide-react";
import { BUSINESS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "icm_admin_token";

const STATUS = {
  new: { label: "New", cls: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacted", cls: "bg-amber-100 text-amber-700" },
  done: { label: "Done", cls: "bg-emerald-100 text-emerald-700" },
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back!");
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0f2452] text-white">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="font-display mt-5 text-2xl font-extrabold text-[#0f2452]">
            Admin Login
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Iqaluit Cleaning &amp; Maintenance
          </p>
        </div>

        <div className="mt-7 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Email
            </label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@email.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#2563eb] px-6 py-3.5 text-base font-semibold text-white hover:bg-[#1d4ed8] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
          </button>
        </div>
        <a
          href="/"
          className="mt-6 block text-center text-sm text-slate-500 hover:text-[#2563eb]"
        >
          ← Back to website
        </a>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, authHeader());
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, authHeader());
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, authHeader());
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = quotes.reduce(
    (acc, q) => {
      acc.total += 1;
      acc[q.status] = (acc[q.status] || 0) + 1;
      return acc;
    },
    { total: 0 }
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-[#0f2452] text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={BUSINESS.logo}
              alt="ICM logo"
              className="h-11 w-11 rounded-lg bg-white object-contain p-0.5"
            />
            <div className="leading-tight">
              <p className="font-display font-extrabold">Quote Requests</p>
              <p className="text-xs text-sky-300 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Admin Dashboard
              </p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["Total", counts.total, "text-[#0f2452]"],
            ["New", counts.new || 0, "text-blue-600"],
            ["Contacted", counts.contacted || 0, "text-amber-600"],
            ["Done", counts.done || 0, "text-emerald-600"],
          ].map(([label, val, cls]) => (
            <div
              key={label}
              className="rounded-2xl border border-slate-200 bg-white p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {label}
              </p>
              <p className={`mt-1 font-display text-3xl font-extrabold ${cls}`}>
                {val}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#2563eb]" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-slate-400">
              <Inbox className="h-12 w-12" />
              <p className="mt-3 font-medium">No quote requests yet.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-quotes-list">
              {quotes.map((q) => (
                <div
                  key={q.id}
                  data-testid="admin-quote-row"
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display text-lg font-bold text-[#0f2452]">
                          {q.name}
                        </h3>
                        <span
                          className={`rounded-full px-3 py-0.5 text-xs font-semibold ${
                            STATUS[q.status]?.cls || STATUS.new.cls
                          }`}
                        >
                          {STATUS[q.status]?.label || q.status}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-0.5 text-xs font-medium text-slate-600">
                          {q.service_type}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                        <a
                          href={`mailto:${q.email}`}
                          className="inline-flex items-center gap-1.5 hover:text-[#2563eb]"
                        >
                          <Mail className="h-4 w-4" /> {q.email}
                        </a>
                        <a
                          href={`tel:${q.phone}`}
                          className="inline-flex items-center gap-1.5 hover:text-[#2563eb]"
                        >
                          <Phone className="h-4 w-4" /> {q.phone}
                        </a>
                      </div>
                      {q.details && (
                        <p className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-xl p-3">
                          {q.details}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-slate-400">
                        {new Date(q.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        data-testid="admin-status-select"
                        value={q.status}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-[#2563eb]"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="done">Done</option>
                      </select>
                      <button
                        onClick={() => remove(q.id)}
                        data-testid="admin-delete-button"
                        aria-label="Delete quote"
                        className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <LoginScreen onLogin={() => setAuthed(true)} />
  );
}
